import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import autoTable from 'jspdf-autotable';
import {
  applyAssessmentPdfBranding,
  createAssessmentPdfPreview,
  downloadAssessmentPdfPreview,
  drawPdfCenteredReportTitle,
  createA4Pdf,
  addA4PdfPage,
  mergePdfAutoTableOptions,
  getEnovaLogoDataUrl,
  PDF_CONTENT_TOP_MM,
  revokeAssessmentPdfPreview,
  AssessmentPdfFullscreenViewer,
  ExportExcelIcon,
  ExportPdfIcon,
} from '../dashboard/downloadQuestionPaperPdf';
import AssessmentPopup from '../dashboard/AssessmentPopup';
import { FiArrowLeft, FiCheckCircle, FiDownload, FiEye } from 'react-icons/fi';
import { useTests } from '../../../hooks/useTests';
import { testService } from '../../../services/testService';
import toast from 'react-hot-toast';
import '../../../styles/Results.css';
import '../assessment-module-mobile.css';
import { AssessmentEditIcon, assessTipProps, useAssessmentContext } from '../AssessmentContext';
import { resolveAssessmentApiBaseUrl } from '../assessmentApiConfig';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  getSubmissionFileLabel,
  normalizeSubmissionFilesList,
  resolveSubmissionFileUrl,
} from '../../../utils/assessmentStudent';

const resolveQuestionPedagogy = (question) =>
  String(question?.pedagogy || question?.knowledge_type || '').trim();

const isMcqQuestion = (question) =>
  question?.type === 'MCQ' ||
  (question?.option_a && String(question.option_a).trim() !== '');

/** Sum marks earned per pedagogy from pack questions and submission answers. */
const computePedagogyMarksForSubmission = (questions, allAnswers) => {
  const marksByPedagogy = {};
  if (!Array.isArray(questions) || questions.length === 0) {
    return marksByPedagogy;
  }

  questions.forEach((q) => {
    const pedagogy = resolveQuestionPedagogy(q);
    if (!pedagogy) return;

    const qMarks = parseFloat(q.marks) || 0;
    let earned = 0;

    if (isMcqQuestion(q)) {
      const selected = allAnswers[q.id] ?? allAnswers[q.question];
      const correctLetter = q.correct_answer || q.answer;
      const optionsMap = {
        A: q.option_a,
        B: q.option_b,
        C: q.option_c,
        D: q.option_d,
      };
      const letter = String(correctLetter || '').trim().toUpperCase();
      const correctText = optionsMap[letter] || correctLetter;
      const selectedStr = selected != null && selected !== '' ? String(selected).trim() : '';
      const isCorrect =
        selectedStr !== '' &&
        (selectedStr.toLowerCase() === String(correctText).toLowerCase() ||
          (letter && selectedStr.toUpperCase() === letter));
      if (isCorrect) earned = qMarks;
    } else {
      earned = parseFloat(allAnswers[`awarded_${q.id}`]) || 0;
    }

    marksByPedagogy[pedagogy] = (marksByPedagogy[pedagogy] || 0) + earned;
  });

  return marksByPedagogy;
};

const SubmissionFileLinks = ({ files, compact = false }) => {
  const list = normalizeSubmissionFilesList(files);
  if (!list.length) return null;

  const apiBase = resolveAssessmentApiBaseUrl();

  return (
    <div className={`assess-mod-submission-files flex flex-wrap items-center gap-1.5 ${compact ? '' : 'mt-1'}`}>
      {list.map((path, idx) => {
        const url = resolveSubmissionFileUrl(path, apiBase);
        const label = getSubmissionFileLabel(path);
        const isExcel = path.toLowerCase().endsWith('.xlsx') || path.toLowerCase().endsWith('.xls');
        return (
          <span
            key={`${path}-${idx}`}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 shadow-sm"
          >
            <span className="text-[9px] font-bold text-slate-500 max-w-[88px] truncate" title={label}>
              {label}
            </span>
            {!isExcel && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-6 h-6 rounded-md flex items-center justify-center text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-100 transition-colors"
                {...assessTipProps(`View ${label}`)}
              >
                <FiEye className="text-xs" />
              </a>
            )}
            <a
              href={url}
              download={label}
              className="w-6 h-6 rounded-md flex items-center justify-center text-emerald-600 hover:bg-emerald-50 border border-transparent hover:border-emerald-100 transition-colors"
              {...assessTipProps(`Download ${label}`)}
            >
              <FiDownload className="text-xs" />
            </a>
          </span>
        );
      })}
    </div>
  );
};

/**
 * Marks field — text input (not type=number) so the mouse wheel cannot change the value.
 * Native non-passive wheel listener as a fallback on the element.
 */
const EvalMarksInput = ({ value, onChange, onFocus, max, className }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;
    const blockWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener('wheel', blockWheel, { passive: false });
    el.addEventListener('mousewheel', blockWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', blockWheel);
      el.removeEventListener('mousewheel', blockWheel);
    };
  }, []);

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      className={`assess-mod-eval-mark-input ${className || ''}`}
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '' || /^\d*\.?\d*$/.test(v)) {
          onChange(v);
        }
      }}
      onFocus={onFocus}
      placeholder="-"
      aria-label={`Marks awarded out of ${max}`}
    />
  );
};

// --- Sub-Components ---

const StatusBadge = ({ status }) => {
  const getColors = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending review': 
      case 'pending': 
        return 'badge-pending';
      case 'completed': 
      case 'evaluated':
      case 'pass': 
        return 'badge-evaluated';
      case 'fail': 
        return 'badge-fail';
      case 'ongoing':
        return 'badge-ongoing';
      default: 
        return 'badge-pending';
    }
  };

  return (
    <span className={`badge-pill ${getColors(status)}`}>
      {status || '-'}
    </span>
  );
};

const StatsCards = ({ stats }) => (
  <div className="assess-mod-stats-row flex flex-col items-end">
    <div className="assess-mod-stats-pill flex items-center gap-4">
      <div className="relative overflow-hidden bg-gradient-to-r from-slate-200/40 to-white border-[1.5px] rounded-[16px] w-[180px] h-[85px] results-card transition-all flex flex-col justify-center px-5">
        <span className="relative z-10 text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-2">Total Mark</span>
        <span className="relative z-10 text-[32px] font-black text-[#1e293b] leading-none">{stats.totalMark || 0}</span>
        <i className="bi bi-activity absolute -right-4 top-1/2 -translate-y-1/2 text-[70px] text-slate-200 opacity-40" />
      </div>
      <div className="relative overflow-hidden bg-gradient-to-r from-[#f0f9ff] to-white rounded-[16px] w-[180px] h-[85px] results-card transition-all flex flex-col justify-center px-5 border border-white">
        <span className="relative z-10 text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-2">Overall Pass %</span>
        <span className="relative z-10 text-[32px] font-black text-[#1e293b] leading-none">{stats.passPct || 0}%</span>
        <i className="bi bi-plus-lg absolute -right-2 top-1/2 -translate-y-1/2 text-[70px] text-slate-100 opacity-60" />
      </div>
      <div className="relative overflow-hidden bg-white rounded-[16px] w-[180px] h-[85px] results-card transition-all flex flex-col justify-center px-5 border border-slate-100">
        <span className="relative z-10 text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-2">Fail Count</span>
        <span className="relative z-10 text-[32px] font-black text-[#1e293b] leading-none">{stats.failCount || 0}</span>
        <i className="bi bi-activity absolute -right-4 top-1/2 -translate-y-1/2 text-[70px] text-slate-100 opacity-50" />
      </div>
      <div className="relative overflow-hidden bg-white rounded-[16px] w-[180px] h-[85px] results-card transition-all flex flex-col justify-center px-5 border border-slate-100">
        <span className="relative z-10 text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-2">Pending Count</span>
        <span className="relative z-10 text-[32px] font-black text-[#1e293b] leading-none">{stats.pendingCount || 0}</span>
        <i className="bi bi-activity absolute -right-4 top-1/2 -translate-y-1/2 text-[70px] text-slate-100 opacity-50" />
      </div>
    </div>
  </div>
);

const DynamicDuration = ({ startedAt, submittedAt, durationText }) => {
  const [display, setDisplay] = useState(durationText);

  useEffect(() => {
    if (submittedAt) {
      setDisplay(durationText);
      return;
    }

    const update = () => {
      if (!startedAt) {
        setDisplay('Invalid time');
        return;
      }

      const start = new Date(startedAt);
      if (isNaN(start.getTime())) {
        setDisplay('Invalid time');
        return;
      }

      const now = new Date();
      // Calculate diff in seconds. Both are local or both are UTC-equivalent in JS engine
      const diff = Math.floor((now.getTime() - start.getTime()) / 1000);

      if (diff >= 0) {
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        const s = diff % 60;
        
        if (h > 0) setDisplay(`${h}h ${m}m ${s}s`);
        else if (m > 0) setDisplay(`${m}m ${s}s`);
        else setDisplay(`${s}s`);
      } else {
        setDisplay('0s');
      }
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startedAt, submittedAt, durationText]);

  return <span>{display}</span>;
};

const RelativeDate = ({ timestamp }) => {
  const [display, setDisplay] = useState('');

  useEffect(() => {
    const update = () => {
      if (!timestamp) return;
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        setDisplay('N/A');
        return;
      }
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

      if (diffInSeconds < 0) setDisplay('Just now');
      else if (diffInSeconds < 60) setDisplay('Just now');
      else if (diffInSeconds < 3600) setDisplay(`${Math.floor(diffInSeconds / 60)}m ago`);
      else if (diffInSeconds < 86400) setDisplay(`${Math.floor(diffInSeconds / 3600)}h ago`);
      else if (diffInSeconds < 172800) setDisplay('Yesterday');
      else setDisplay(timestamp.split(' ')[0]);
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [timestamp]);

  return <span title={timestamp}>{display}</span>;
};

const getMcqOptionText = (question, letter) => {
  const key = String(letter || '').trim().toUpperCase();
  const map = {
    A: question.option_a,
    B: question.option_b,
    C: question.option_c,
    D: question.option_d,
  };
  return String(map[key] ?? '').trim();
};

const formatMcqAnswerLabel = (question, raw) => {
  const value = String(raw ?? '').trim();
  if (!value) return 'No answer';
  const upper = value.toUpperCase();
  if (['A', 'B', 'C', 'D'].includes(upper)) {
    const text = getMcqOptionText(question, upper);
    return text ? `${upper}. ${text}` : upper;
  }
  return value;
};

const isMcqAnswerCorrect = (question, selected) => {
  const selectedStr = selected != null && selected !== '' ? String(selected).trim() : '';
  if (!selectedStr) return false;

  const correctLetter = String(question.correct_answer || question.answer || '').trim().toUpperCase();
  const letter = correctLetter.length === 1 ? correctLetter : '';
  const correctText = letter ? getMcqOptionText(question, letter) : correctLetter;

  return (
    selectedStr.toLowerCase() === String(correctText).toLowerCase() ||
    (letter && selectedStr.toUpperCase() === letter)
  );
};

const buildAnswerSheetItems = (questions, allAnswers) =>
  (questions || []).map((q) => {
    const selected = allAnswers[q.id] ?? allAnswers[q.question];
    const maxMarks = parseFloat(q.marks) || 1;
    const isMcq = String(q.type || '').toUpperCase() === 'MCQ';

    if (isMcq) {
      const correctLetter = String(q.correct_answer || q.answer || '').trim().toUpperCase();
      const correctLabel = formatMcqAnswerLabel(
        q,
        correctLetter.length === 1 ? correctLetter : q.correct_answer
      );
      const correct = isMcqAnswerCorrect(q, selected);

      return {
        id: q.id,
        type: 'MCQ',
        question: q.question || q.text,
        marks: maxMarks,
        candidate_answer: formatMcqAnswerLabel(q, selected),
        model_answer: correctLabel,
        awarded_marks: correct ? maxMarks : 0,
        is_correct: correct,
      };
    }

    return {
      id: q.id,
      type: 'DESCRIPTIVE',
      question: q.question || q.text,
      marks: maxMarks,
      candidate_answer: selected ? String(selected) : 'No answer provided.',
      model_answer: q.correct_answer || '—',
      awarded_marks: parseFloat(allAnswers[`awarded_${q.id}`]) || 0,
    };
  });

const FilterSelect = ({ value, onChange, children }) => (
  <div className="assess-mod-filter-select-wrap">
    <select className="filter-select" value={value} onChange={onChange}>
      {children}
    </select>
    <i className="bi bi-chevron-down assess-mod-filter-select-chevron" aria-hidden />
  </div>
);

const FilterBar = ({ filters, onFilterChange, onSearchChange, options }) => {
  return (
    <div className="filter-bar assess-mod-filter-bar assess-mod-results-filter-bar">
      <FilterSelect value={filters.type} onChange={(e) => onFilterChange('type', e.target.value)}>
        <option value="">All Test Types</option>
        {options.types.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </FilterSelect>

      <FilterSelect value={filters.testName} onChange={(e) => onFilterChange('testName', e.target.value)}>
        <option value="">All Tests</option>
        {options.tests.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </FilterSelect>

      <FilterSelect value={filters.group} onChange={(e) => onFilterChange('group', e.target.value)}>
        <option value="">All Groups</option>
        {options.groups.map((g) => (
          <option key={g} value={g}>
            {g}
          </option>
        ))}
      </FilterSelect>

      <FilterSelect value={filters.role} onChange={(e) => onFilterChange('role', e.target.value)}>
        <option value="">All Roles</option>
        {options.roles.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </FilterSelect>

      <FilterSelect value={filters.date} onChange={(e) => onFilterChange('date', e.target.value)}>
        <option value="">All Dates</option>
        {options.dates.map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </FilterSelect>

      <div className="search-wrapper">
        <i className="bi bi-search search-icon-v2"></i>
        <input 
          type="text" 
          className="search-input-v2"
          placeholder="Search candidate..."
          value={filters.search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
    </div>
  );
};

const CandidateTable = ({ data, onEvaluate, onViewAnswerSheet, sortConfig, onSort, rankOffset = 0 }) => {
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-caret-up text-slate-200 ml-1"></i>;
    return sortConfig.direction === 'asc' 
      ? <i className="bi bi-caret-up-fill text-indigo-600 ml-1"></i>
      : <i className="bi bi-caret-down-fill text-indigo-600 ml-1"></i>;
  };

  return (
    <div className="table-responsive assess-mod-results-table-wrap assess-mod-table-wrap">
      <table className="leaderboard-table">
        <thead>
          <tr>
            <th className="text-center w-12">Rank</th>
            <th className="cursor-pointer select-none" onClick={() => onSort('candidate_name')}>
              <div className="flex items-center">Candidate Name {getSortIcon('candidate_name')}</div>
            </th>
            <th className="text-center cursor-pointer select-none" onClick={() => onSort('test_type')}>
              <div className="flex items-center justify-center">Test Type {getSortIcon('test_type')}</div>
            </th>
            <th className="text-center cursor-pointer select-none" onClick={() => onSort('role')}>
              <div className="flex items-center justify-center">Role {getSortIcon('role')}</div>
            </th>
            <th className="text-center cursor-pointer select-none" onClick={() => onSort('displayStatus')}>
              <div className="flex items-center justify-center">Status / Duration {getSortIcon('displayStatus')}</div>
            </th>
            <th className="text-center cursor-pointer select-none" onClick={() => onSort('final_score')}>
              <div className="flex items-center justify-center">Marks {getSortIcon('final_score')}</div>
            </th>
            <th className="text-center cursor-pointer select-none" onClick={() => onSort('overall_pct')}>
              <div className="flex items-center justify-center">Overall % {getSortIcon('overall_pct')}</div>
            </th>
            <th className="text-center cursor-pointer select-none" onClick={() => onSort('status')}>
              <div className="flex items-center justify-center">Pass / Fail {getSortIcon('status')}</div>
            </th>
            <th className="text-center">Evaluate</th>
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? data.map((item, index) => (
            <tr key={item.key || index}>
              <td className="text-center text-[11px] font-black text-slate-600">
                {rankOffset + index + 1}
              </td>
              <td className="candidate-name">
                <div className="flex flex-col">
                  <span className="text-[14px] font-bold text-slate-800">{item.candidate_name}</span>
                  <span className="text-[10px] text-slate-400 font-black uppercase tracking-wider">
                    <RelativeDate timestamp={item.rawSub.submitted_at || item.rawSub.started_at} />
                  </span>
                </div>
              </td>
              <td className="text-center text-slate-400 font-bold">{item.test_type || 'General'}</td>
              <td className="text-center text-slate-700 font-bold uppercase">{item.role || 'Candidate'}</td>
              <td className="text-center">
                <div className="flex flex-col items-center justify-center gap-1.5">
                  <StatusBadge status={item.displayStatus} />
                  <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-100">
                    <i className="bi bi-clock-history text-[10px]"></i>
                    <DynamicDuration 
                      startedAt={item.rawSub.started_at} 
                      submittedAt={item.rawSub.submitted_at} 
                      durationText={item.duration_text} 
                    />
                  </div>
                </div>
              </td>
              <td className="text-center font-black text-indigo-600 text-[14px]">
                <span className="inline-block px-3 py-1.5">{item.marks_text}</span>
              </td>
              <td className="text-center font-black text-slate-800 text-[14px]">{item.overall_pct}%</td>
              <td className="text-center">
                {item.displayStatus === 'Evaluated' ? (
                  <StatusBadge status={item.status} />
                ) : (
                  <span className="text-slate-200 font-black text-[10px]">-</span>
                )}
              </td>
               <td className="text-center">
                  {item.displayStatus === 'Evaluated' ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100 shrink-0">
                         <i className="bi bi-patch-check-fill text-[11px]"></i>
                         <span className="text-[10px] font-black uppercase tracking-widest">Evaluated</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => onViewAnswerSheet(item)}
                        className="w-8 h-8 rounded-xl bg-white border border-indigo-100 text-indigo-600 flex items-center justify-center transition-all shadow-sm hover:bg-indigo-50 hover:border-indigo-200"
                        {...assessTipProps('View answer sheet')}
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => !item.isMcqOnly && onEvaluate(item)}
                        disabled={item.isMcqOnly}
                        className={`w-8 h-8 rounded-xl bg-white border flex items-center justify-center transition-all shadow-sm ${
                          item.isMcqOnly
                            ? 'opacity-30 cursor-not-allowed border-slate-200 text-slate-300 grayscale'
                            : 'border-slate-100 text-slate-400 hover:text-slate-800 hover:border-slate-300'
                        }`}
                        {...assessTipProps(
                          item.isMcqOnly ? 'No manual review required (MCQ only)' : 'Edit evaluation'
                        )}
                      >
                        <AssessmentEditIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={() => !item.isMcqOnly && onEvaluate(item)}
                      className={`btn-evaluate-action mx-auto ${item.isMcqOnly ? 'opacity-30 cursor-not-allowed grayscale' : ''}`}
                      {...assessTipProps(
                        item.isMcqOnly ? 'No manual review required (MCQ only)' : 'Evaluate subjective answers'
                      )}
                    >
                      <AssessmentEditIcon className="w-3 h-3" />
                    </button>
                  )}
               </td>
            </tr>
          )) : (
            <tr>
              <td colSpan="9" className="py-8 text-center text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                No candidate data found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const ResultsPagination = ({ currentPage, totalPages, totalItems, pageSize, onPageChange }) => {
  if (totalItems <= pageSize) {
    return null;
  }

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  const visiblePages = pages.length <= 7
    ? pages
    : pages.filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1);

  return (
    <div className="assess-mod-results-pagination px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-100 bg-white">
      <div className="assess-mod-inventory-pagination text-sm font-semibold text-slate-500">
        Showing {start} to {end} of {totalItems} entries
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className={`assess-mod-inventory-pagination flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
            currentPage <= 1
              ? 'bg-slate-50 border-slate-50 text-slate-300 cursor-not-allowed'
              : 'bg-white border-slate-100 text-slate-500 hover:border-red-200 hover:text-red-500 shadow-sm'
          }`}
        >
          Previous
        </button>
        {visiblePages.map((page, idx) => {
          const prev = visiblePages[idx - 1];
          const showEllipsis = prev && page - prev > 1;
          return (
            <React.Fragment key={page}>
              {showEllipsis && <span className="px-1 text-slate-300">…</span>}
              <button
                type="button"
                onClick={() => onPageChange(page)}
                className={`assess-mod-inventory-pagination w-9 h-9 flex items-center justify-center rounded-xl text-sm font-bold transition-all ${
                  currentPage === page
                    ? 'bg-red-600 text-white shadow-md shadow-red-100'
                    : 'bg-white border border-slate-100 text-slate-500 hover:border-red-200 hover:text-red-500'
                }`}
              >
                {page}
              </button>
            </React.Fragment>
          );
        })}
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className={`assess-mod-inventory-pagination flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
            currentPage >= totalPages
              ? 'bg-slate-50 border-slate-50 text-slate-300 cursor-not-allowed'
              : 'bg-white border-slate-100 text-slate-500 hover:border-red-200 hover:text-red-500 shadow-sm'
          }`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

// --- Report export (Excel / PDF) & format buttons ---

/** Dark blue header (group + table column headers) */
const EXCEL_HEADER_FILL = 'FF1F3864';
/** Light blue field-name labels (Test name, Group name, …) */
const EXCEL_FIELD_LABEL_FILL = 'FFD9E2F3';
const EXCEL_HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
const EXCEL_FIELD_LABEL_FONT = { bold: true, color: { argb: 'FF1E293B' }, size: 10 };
const EXCEL_TITLE_FONT = { bold: true, color: { argb: 'FF1F3864' }, size: 16 };
const EXCEL_BODY_FONT = { size: 10, color: { argb: 'FF1E293B' } };

const excelCellBorder = () => ({
  top: { style: 'thin', color: { argb: 'FF000000' } },
  left: { style: 'thin', color: { argb: 'FF000000' } },
  bottom: { style: 'thin', color: { argb: 'FF000000' } },
  right: { style: 'thin', color: { argb: 'FF000000' } },
});

const excelSetFill = (cell, argb) => {
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
};

const writeExcelAoa = (worksheet, aoa) => {
  aoa.forEach((row, rowIndex) => {
    row.forEach((value, colIndex) => {
      const cell = worksheet.getCell(rowIndex + 1, colIndex + 1);
      cell.value = value ?? '';
    });
  });
};

const applyExcelTitleRow = (worksheet, titleRowIndex, colCount) => {
  const row = titleRowIndex + 1;
  if (colCount > 1) {
    worksheet.mergeCells(row, 1, row, colCount);
  }
  const cell = worksheet.getCell(row, 1);
  cell.font = { ...EXCEL_TITLE_FONT };
  cell.alignment = { horizontal: 'center', vertical: 'middle' };
  worksheet.getRow(row).height = 24;
};

const applyExcelGroupHeaderRow = (worksheet, rowIndex, colCount) => {
  const row = rowIndex + 1;
  for (let c = 1; c <= colCount; c += 1) {
    const cell = worksheet.getCell(row, c);
    excelSetFill(cell, EXCEL_HEADER_FILL);
    cell.font = { ...EXCEL_HEADER_FONT };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = excelCellBorder();
  }
  worksheet.getRow(row).height = 22;
};

const applyExcelMetaBlock = (worksheet, startRowIndex, endRowIndex, colCount) => {
  for (let r = startRowIndex; r <= endRowIndex; r += 1) {
    const excelRow = r + 1;
    const labelCell = worksheet.getCell(excelRow, 1);
    excelSetFill(labelCell, EXCEL_FIELD_LABEL_FILL);
    labelCell.font = { ...EXCEL_FIELD_LABEL_FONT };
    labelCell.alignment = { horizontal: 'left', vertical: 'middle' };
    labelCell.border = excelCellBorder();

    for (let c = 2; c <= colCount; c += 1) {
      const valueCell = worksheet.getCell(excelRow, c);
      valueCell.font = { ...EXCEL_BODY_FONT };
      valueCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      valueCell.border = excelCellBorder();
    }
    worksheet.getRow(excelRow).height = 20;
  }
};

const applyExcelTableSection = (worksheet, headerRowIndex, colCount, lastRowIndex) => {
  const headerExcelRow = headerRowIndex + 1;
  for (let c = 1; c <= colCount; c += 1) {
    const cell = worksheet.getCell(headerExcelRow, c);
    excelSetFill(cell, EXCEL_HEADER_FILL);
    cell.font = { ...EXCEL_HEADER_FONT };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = excelCellBorder();
  }
  worksheet.getRow(headerExcelRow).height = 24;

  for (let r = headerRowIndex + 1; r <= lastRowIndex; r += 1) {
    const excelRow = r + 1;
    for (let c = 1; c <= colCount; c += 1) {
      const cell = worksheet.getCell(excelRow, c);
      cell.font = { ...EXCEL_BODY_FONT };
      cell.alignment = {
        horizontal: c === 2 ? 'left' : 'center',
        vertical: 'middle',
        wrapText: true,
      };
      cell.border = excelCellBorder();
    }
    worksheet.getRow(excelRow).height = 18;
  }
};

const setExcelColumnWidths = (worksheet, colCount, options = {}) => {
  for (let c = 1; c <= colCount; c += 1) {
    if (options.totalMarksColIndex != null && c - 1 === options.totalMarksColIndex) {
      worksheet.getColumn(c).width = 14;
    } else if (c === 1) {
      worksheet.getColumn(c).width = 8;
    } else if (c === 2) {
      worksheet.getColumn(c).width = 28;
    } else {
      worksheet.getColumn(c).width = 18;
    }
  }
};

const populateAssessmentReportWorksheet = (worksheet, layout) => {
  const {
    aoa,
    colCount,
    metaColCount = colCount,
    titleRowIndex,
    groupHeaderRowIndex = null,
    matrixRowStart,
    matrixRowEnd,
    tableHeaderRowIndex,
    totalMarksColIndex,
  } = layout;

  writeExcelAoa(worksheet, aoa);
  applyExcelTitleRow(worksheet, titleRowIndex, colCount);

  if (groupHeaderRowIndex != null) {
    applyExcelGroupHeaderRow(worksheet, groupHeaderRowIndex, metaColCount);
  }

  applyExcelMetaBlock(worksheet, matrixRowStart, matrixRowEnd, metaColCount);

  const lastRowIndex = aoa.length - 1;
  applyExcelTableSection(worksheet, tableHeaderRowIndex, colCount, lastRowIndex);

  setExcelColumnWidths(worksheet, colCount, { totalMarksColIndex });
};

const downloadAssessmentReportExcelWorkbook = async ({ sheets, filename }) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Assessment Module';
  workbook.created = new Date();

  sheets.forEach(({ name, layout }) => {
    const worksheet = workbook.addWorksheet(name, {
      views: [{ state: 'frozen', ySplit: layout.tableHeaderRowIndex + 1 }],
    });
    populateAssessmentReportWorksheet(worksheet, layout);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename
  );
};

const padReportRow = (row, width) => {
  const next = [...row];
  while (next.length < width) next.push('');
  return next;
};

const pickSingleReportValue = (values = []) => {
  const unique = [...new Set(values.filter((v) => v != null && String(v).trim() !== ''))].sort();
  return unique[0] || '';
};

const formatReportDate = (value) => {
  if (!value) return '';
  const normalized = String(value).trim();
  if (!normalized || normalized === 'Not scheduled' || normalized === 'N/A') {
    return normalized || 'Not scheduled';
  }

  const isoMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }

  const dmyMatch = normalized.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmyMatch) return normalized;

  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) {
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }

  return normalized;
};

const resolveAssessmentReportMeta = ({
  tests = [],
  assessmentId,
  filters = {},
  data = [],
  totalQuestions: totalQuestionsOverride,
  examType: examTypeOverride,
}) => {
  const activeTest =
    (assessmentId && tests.find((t) => String(t.id) === String(assessmentId))) ||
    (filters.testName && tests.find((t) => t.name === filters.testName)) ||
    (data[0]?.testId && tests.find((t) => String(t.id) === String(data[0].testId))) ||
    null;

  const testName = filters.testName || activeTest?.name || data[0]?.test_name || 'Assessment Report';

  const packs = activeTest?.test_packs || activeTest?.testPacks || [];
  const relevantPacks = filters.group
    ? packs.filter((p) => (p.pack_name || p.name) === filters.group)
    : packs;

  let examDate = filters.date || '';
  if (!examDate) {
    examDate = pickSingleReportValue(relevantPacks.map((p) => p.scheduled_date).filter(Boolean));
  }
  if (!examDate) {
    examDate = pickSingleReportValue(data.map((r) => r.date_ymd).filter((d) => d && d !== 'N/A'));
  }
  examDate = examDate ? formatReportDate(examDate) : 'Not scheduled';

  const durationValue = pickSingleReportValue(
    relevantPacks.map((p) => Number(p.duration)).filter((n) => n > 0)
  );
  const totalDuration = durationValue ? `${durationValue} Minutes` : 'Not specified';

  const totalQuestions =
    totalQuestionsOverride ??
    (data.length ? Math.max(...data.map((r) => Number(r.total_questions) || 0)) : 0);

  const examType =
    examTypeOverride ??
    filters.type ??
    data[0]?.test_type ??
    activeTest?.category ??
    activeTest?.type ??
    'General';

  return {
    testName,
    examDate,
    totalDuration,
    totalQuestions,
    examType,
    generatedAt: new Date().toLocaleString(),
    candidateCount: data.length,
  };
};

const groupExportEntries = (data = []) => {
  const map = new Map();
  data.forEach((item) => {
    const key = String(item.group_name || 'General Group').trim() || 'General Group';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  });
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
};

const resolveGroupExportMeta = ({
  tests = [],
  assessmentId,
  filters = {},
  groupName,
  groupData = [],
  reportMeta = {},
}) =>
  resolveAssessmentReportMeta({
    tests,
    assessmentId,
    filters: { ...filters, group: groupName },
    data: groupData,
    totalQuestions: groupData.length
      ? Math.max(...groupData.map((r) => Number(r.total_questions) || 0))
      : 0,
    examType: reportMeta.examType,
  });

const resolveQuestionPedagogyLabel = (question) =>
  String(question?.pedagogy ?? question?.knowledge_type ?? '').trim();

const collectPedagogyColumns = (data = [], exportContext = {}) => {
  const { tests = [], assessmentId, filters = {} } = exportContext;
  const labelByKey = new Map();

  const register = (raw) => {
    const label = String(raw ?? '').trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (!labelByKey.has(key)) {
      labelByKey.set(key, label);
    }
  };

  data.forEach((row) => {
    if (Array.isArray(row.pack_questions)) {
      row.pack_questions.forEach((q) => register(resolveQuestionPedagogyLabel(q)));
    }
    const marks = row.pedagogy_marks;
    if (marks && typeof marks === 'object') {
      Object.keys(marks).forEach((name) => register(name));
    }
  });

  const activeTest =
    (assessmentId && tests.find((t) => String(t.id) === String(assessmentId))) ||
    (filters.testName && tests.find((t) => t.name === filters.testName)) ||
    (data[0]?.testId && tests.find((t) => String(t.id) === String(data[0].testId))) ||
    null;

  const packs = activeTest?.test_packs || activeTest?.testPacks || [];
  const relevantPacks = filters.group
    ? packs.filter((p) => (p.pack_name || p.name) === filters.group)
    : packs;

  relevantPacks.forEach((pack) => {
    const questions = pack?.questions || [];
    questions.forEach((q) => register(resolveQuestionPedagogyLabel(q)));
  });

  return [...labelByKey.values()].sort((a, b) => a.localeCompare(b));
};

const getPedagogyMarkForColumn = (pedagogyMarks, columnLabel) => {
  if (!pedagogyMarks || typeof pedagogyMarks !== 'object') return 0;
  if (pedagogyMarks[columnLabel] != null) {
    return Number(pedagogyMarks[columnLabel]) || 0;
  }
  const target = String(columnLabel).trim().toLowerCase();
  const match = Object.entries(pedagogyMarks).find(
    ([name]) => String(name).trim().toLowerCase() === target
  );
  return match ? Number(match[1]) || 0 : 0;
};

const hasPedagogyExportData = (data = [], exportContext = {}) =>
  collectPedagogyColumns(data, exportContext).length > 0;

const formatExportCellValue = (fieldId, val) => {
  if (val === null || val === undefined) return '';
  const text = String(val).trim();
  if (!text) return '';
  if (fieldId === 'date_ymd') {
    return formatReportDate(text) || text;
  }
  return text;
};

const mapRowsForExportFields = (items, selectedFields) =>
  items.map((item, index) => [
    index + 1,
    ...selectedFields.map((f) => formatExportCellValue(f.id, item[f.id])),
  ]);

const sortCandidatesForExport = (items = []) =>
  [...items].sort((a, b) => {
    const markA = Number(a.final_score) || 0;
    const markB = Number(b.final_score) || 0;
    if (markA !== markB) return markB - markA;
    const timeA = Number(a.duration_sec) || 0;
    const timeB = Number(b.duration_sec) || 0;
    return timeA - timeB;
  });

const sortPedagogyCandidatesForExport = (items = [], pedagogyColumns = []) =>
  [...items].sort((a, b) => {
    const totalA = pedagogyColumns.reduce(
      (sum, label) => sum + getPedagogyMarkForColumn(a.pedagogy_marks, label),
      0
    );
    const totalB = pedagogyColumns.reduce(
      (sum, label) => sum + getPedagogyMarkForColumn(b.pedagogy_marks, label),
      0
    );
    if (totalA !== totalB) return totalB - totalA;
    const timeA = Number(a.duration_sec) || 0;
    const timeB = Number(b.duration_sec) || 0;
    return timeA - timeB;
  });


const buildExportGroupColumns = (groups, { tests, assessmentId, filters, reportMeta }) =>
  groups.map(([groupName, groupData]) => {
    const groupMeta = resolveGroupExportMeta({
      tests,
      assessmentId,
      filters,
      groupName,
      groupData,
      reportMeta,
    });
    return {
      groupName,
      testName: groupMeta.testName,
      examDate: groupMeta.examDate,
      totalDuration: groupMeta.totalDuration,
    };
  });

const buildGroupSummaryMatrixRows = (groupColumns) => {
  if (groupColumns.length === 0) {
    return {
      groupHeaderRow: null,
      labelRows: [
        ['Test name:', '—'],
        ['Group name:', '—'],
        ['Date:', '—'],
        ['Duration:', '—'],
      ],
    };
  }
  return {
    groupHeaderRow: null,
    labelRows: [
      ['Test name:', ...groupColumns.map((g) => g.testName)],
      ['Group name:', ...groupColumns.map((g) => g.groupName)],
      ['Date:', ...groupColumns.map((g) => g.examDate)],
      ['Duration:', ...groupColumns.map((g) => g.totalDuration)],
    ],
  };
};

const computeExportColCount = (selectedFieldCount, groupCount) =>
  Math.max(selectedFieldCount, groupCount + 1, 2);

const flattenSortedExportGroups = (groups, sortGroupItems) =>
  groups.flatMap(([, groupData]) => sortGroupItems(groupData));

const buildAssessmentReportSheet = ({
  data = [],
  allFields = [],
  selectedFieldIds = [],
  reportMeta = {},
  tests = [],
  assessmentId,
  filters = {},
}) => {
  const selectedFields = allFields.filter((f) => selectedFieldIds.includes(f.id));
  if (selectedFields.length === 0) {
    throw new Error('No columns selected');
  }

  const meta = {
    testName: reportMeta.testName || 'Assessment Report',
    examDate: reportMeta.examDate || '—',
    totalDuration: reportMeta.totalDuration || '—',
    totalQuestions: reportMeta.totalQuestions ?? '—',
    examType: reportMeta.examType || '—',
    generatedAt: reportMeta.generatedAt || new Date().toLocaleString(),
    candidateCount: reportMeta.candidateCount ?? data.length,
  };

  const headers = ['S.No', ...selectedFields.map((f) => f.label)];
  const rows = mapRowsForExportFields(data, selectedFields);
  const groups = groupExportEntries(data);
  const groupColumns = buildExportGroupColumns(groups, { tests, assessmentId, filters, reportMeta });
  const colCount = computeExportColCount(selectedFields.length + 1, groupColumns.length);
  const metaColCount = Math.max(groupColumns.length + 1, 2);
  const titleRowIndex = 0;
  const { groupHeaderRow, labelRows } = buildGroupSummaryMatrixRows(groupColumns);
  const groupHeaderRowIndex = groupHeaderRow ? 2 : null;
  const matrixRowStart = groupHeaderRow ? 3 : 2;
  const matrixRowEnd = matrixRowStart + labelRows.length - 1;
  const tableHeaderRowIndex = matrixRowEnd + 2;
  const sortedAllData = flattenSortedExportGroups(groups, sortCandidatesForExport);

  const aoa = [
    padReportRow(['ASSESSMENT RESULTS REPORT'], colCount),
    padReportRow([''], colCount),
    ...(groupHeaderRow ? [padReportRow(groupHeaderRow, metaColCount)] : []),
    ...labelRows.map((row) => padReportRow(row, metaColCount)),
    padReportRow([''], colCount),
    padReportRow(['S.NO', ...selectedFields.map((f) => f.label.toUpperCase())], colCount),
    ...mapRowsForExportFields(sortedAllData, selectedFields).map((row) => padReportRow(row, colCount)),
  ];

  return {
    meta,
    selectedFields,
    headers,
    rows,
    aoa,
    colCount,
    metaColCount,
    titleRowIndex,
    groupHeaderRowIndex,
    matrixRowStart,
    matrixRowEnd,
    tableHeaderRowIndex,
  };
};

const buildPedagogyMarksSheet = ({ data = [], reportMeta = {}, exportContext = {} }) => {
  const pedagogyColumns = collectPedagogyColumns(data, exportContext);
  if (pedagogyColumns.length === 0) {
    return null;
  }

  const totalMarksColumnLabel = 'Total Marks';
  const tableHeaders = ['S.No', 'Student Name', ...pedagogyColumns, totalMarksColumnLabel];
  const groups = groupExportEntries(data);
  const { tests = [], assessmentId, filters = {} } = exportContext;
  const groupColumns = buildExportGroupColumns(groups, { tests, assessmentId, filters, reportMeta });
  const colCount = Math.max(computeExportColCount(tableHeaders.length, groupColumns.length), 3);
  const metaColCount = Math.max(groupColumns.length + 1, 2);
  const titleRowIndex = 0;
  const { groupHeaderRow, labelRows } = buildGroupSummaryMatrixRows(groupColumns);
  const groupHeaderRowIndex = groupHeaderRow ? 2 : null;
  const matrixRowStart = groupHeaderRow ? 3 : 2;
  const matrixRowEnd = matrixRowStart + labelRows.length - 1;
  const tableHeaderRowIndex = matrixRowEnd + 2;
  const sortPedagogyGroup = (groupData) =>
    sortPedagogyCandidatesForExport(groupData, pedagogyColumns);
  const sortedAllData = flattenSortedExportGroups(groups, sortPedagogyGroup);

  const tableRows = sortedAllData.map((row, index) => {
    const pedagogyScores = pedagogyColumns.map((label) =>
      getPedagogyMarkForColumn(row.pedagogy_marks, label)
    );
    const pedagogyTotal = pedagogyScores.reduce((sum, score) => sum + (Number(score) || 0), 0);
    return [index + 1, row.candidate_name || 'Anonymous', ...pedagogyScores, pedagogyTotal];
  });

  const aoa = [
    padReportRow(['PEDAGOGY-WISE MARKS REPORT'], colCount),
    padReportRow([''], colCount),
    ...(groupHeaderRow ? [padReportRow(groupHeaderRow, metaColCount)] : []),
    ...labelRows.map((row) => padReportRow(row, metaColCount)),
    padReportRow([''], colCount),
    padReportRow(tableHeaders.map((h) => String(h).toUpperCase()), colCount),
    ...tableRows.map((row) => padReportRow(row, colCount)),
  ];

  return {
    aoa,
    colCount,
    metaColCount,
    titleRowIndex,
    groupHeaderRowIndex,
    matrixRowStart,
    matrixRowEnd,
    tableHeaderRowIndex,
    pedagogyColumns,
    totalMarksColIndex: colCount - 1,
  };
};

const downloadAssessmentReportExcel = async (params) => {
  const exportType = params.exportType || 'excel';

  if (exportType !== 'excel') {
    throw new Error('Unsupported export type');
  }

  const sheet1 = buildAssessmentReportSheet(params);
  const sheets = [
    {
      name: 'Overall Marks',
      layout: {
        aoa: sheet1.aoa,
        colCount: sheet1.colCount,
        metaColCount: sheet1.metaColCount,
        titleRowIndex: sheet1.titleRowIndex,
        groupHeaderRowIndex: sheet1.groupHeaderRowIndex,
        matrixRowStart: sheet1.matrixRowStart,
        matrixRowEnd: sheet1.matrixRowEnd,
        tableHeaderRowIndex: sheet1.tableHeaderRowIndex,
      },
    },
  ];

  const exportContext = {
    tests: params.tests || [],
    assessmentId: params.assessmentId,
    filters: params.filters || {},
  };

  if (hasPedagogyExportData(params.data, exportContext)) {
    const sheet2 = buildPedagogyMarksSheet({ ...params, exportContext });
    if (sheet2) {
      sheets.push({
        name: 'Pedagogy Marks',
        layout: {
          aoa: sheet2.aoa,
          colCount: sheet2.colCount,
          metaColCount: sheet2.metaColCount,
          titleRowIndex: sheet2.titleRowIndex,
          groupHeaderRowIndex: sheet2.groupHeaderRowIndex,
          matrixRowStart: sheet2.matrixRowStart,
          matrixRowEnd: sheet2.matrixRowEnd,
          tableHeaderRowIndex: sheet2.tableHeaderRowIndex,
          totalMarksColIndex: sheet2.totalMarksColIndex,
        },
      });
    }
  }

  const safeName = String(sheet1.meta.testName)
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 40) || 'Assessment';
  const filename = `Assessment_Report_${safeName}_${Date.now()}.xlsx`;

  await downloadAssessmentReportExcelWorkbook({ sheets, filename });
};

const sanitizeReportFilename = (testName) =>
  String(testName)
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 40) || 'Assessment';

const PDF_TABLE_MARGIN = 14;

const PDF_TABLE_BASE = {
  styles: {
    fontSize: 8,
    cellPadding: 2.5,
    overflow: 'linebreak',
    textColor: [30, 41, 59],
  },
  headStyles: {
    fillColor: [30, 41, 59],
    textColor: [255, 255, 255],
    fontStyle: 'bold',
    halign: 'center',
  },
  alternateRowStyles: { fillColor: [248, 250, 252] },
  theme: 'grid',
};

const getPdfCompactTableStyles = (columnCount) => {
  if (columnCount > 10) {
    return { fontSize: 6, cellPadding: 1, overflow: 'linebreak' };
  }
  if (columnCount > 7) {
    return { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' };
  }
  return null;
};

const ensurePdfPageSpace = (doc, y, minSpace = 42) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y > pageHeight - minSpace) {
    addA4PdfPage(doc, 'portrait');
    return PDF_CONTENT_TOP_MM;
  }
  return y;
};

const drawPdfReportTitle = (doc, title) => drawPdfCenteredReportTitle(doc, title);

const drawPdfDetailLines = (doc, detailLines, pageWidth, startY) => {
  let y = startY;
  doc.setFontSize(10);

  detailLines.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(51, 65, 85);
    doc.text(label, PDF_TABLE_MARGIN, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 41, 59);
    const valueLines = doc.splitTextToSize(String(value ?? ''), pageWidth - PDF_TABLE_MARGIN - 58);
    doc.text(valueLines, PDF_TABLE_MARGIN + 52, y);
    y += Math.max(7, valueLines.length * 5);
  });

  return y + 4;
};

const drawPdfGroupSummaryMatrix = (doc, groupColumns, startY) => {
  if (groupColumns.length === 0) {
    return drawPdfDetailLines(
      doc,
      [
        ['Test name:', '—'],
        ['Group name:', '—'],
        ['Date:', '—'],
        ['Duration:', '—'],
      ],
      doc.internal.pageSize.getWidth(),
      startY
    );
  }

  autoTable(
    doc,
    mergePdfAutoTableOptions(doc, {
      startY,
      body: [
        ['Test name:', ...groupColumns.map((g) => g.testName)],
        ['Group name:', ...groupColumns.map((g) => g.groupName)],
        ['Date:', ...groupColumns.map((g) => g.examDate)],
        ['Duration:', ...groupColumns.map((g) => g.totalDuration)],
      ],
      styles: {
        ...PDF_TABLE_BASE.styles,
        fontSize: 8,
        cellPadding: 2.5,
      },
      columnStyles: {
        0: {
          fillColor: [226, 232, 240],
          textColor: [51, 65, 85],
          fontStyle: 'bold',
          halign: 'left',
        },
      },
      theme: 'grid',
    }, PDF_TABLE_MARGIN)
  );

  return (doc.lastAutoTable?.finalY ?? startY) + 8;
};

const drawPdfDataTable = (doc, headers, body, startY) => {
  const columnCount =
    Array.isArray(headers) && headers.length > 0 ? headers.length : body[0]?.length || 1;
  const compactStyles = getPdfCompactTableStyles(columnCount);

  const tableConfig = mergePdfAutoTableOptions(
    doc,
    {
      ...PDF_TABLE_BASE,
      startY,
      body,
      showHead: 'everyPage',
      ...(compactStyles
        ? {
            styles: { ...PDF_TABLE_BASE.styles, ...compactStyles },
            headStyles: { ...PDF_TABLE_BASE.headStyles, ...compactStyles },
          }
        : {}),
    },
    PDF_TABLE_MARGIN
  );

  if (Array.isArray(headers) && headers.length > 0) {
    tableConfig.head = [headers.map((h) => String(h).toUpperCase())];
  }

  autoTable(doc, tableConfig);
  return (doc.lastAutoTable?.finalY ?? startY) + 8;
};

const buildAssessmentReportPdfDoc = async ({
  data = [],
  allFields = [],
  selectedFieldIds = [],
  reportMeta = {},
  tests = [],
  assessmentId,
  filters = {},
}) => {
  const selectedFields = allFields.filter((f) => selectedFieldIds.includes(f.id));
  if (selectedFields.length === 0) {
    throw new Error('No columns selected');
  }

  const meta = {
    testName: reportMeta.testName || 'Assessment Report',
    examDate: reportMeta.examDate || '—',
    totalDuration: reportMeta.totalDuration || '—',
    totalQuestions: reportMeta.totalQuestions ?? '—',
  };

  const groups = groupExportEntries(data);
  const groupColumns = buildExportGroupColumns(groups, { tests, assessmentId, filters, reportMeta });
  const doc = createA4Pdf('portrait');

  let y = drawPdfReportTitle(doc, 'ASSESSMENT RESULTS REPORT');
  y = drawPdfGroupSummaryMatrix(doc, groupColumns, y);
  y += 4;

  const sortedAllData = flattenSortedExportGroups(groups, sortCandidatesForExport);
  const headers = ['S.No', ...selectedFields.map((f) => f.label)];
  const body = mapRowsForExportFields(sortedAllData, selectedFields);
  drawPdfDataTable(doc, headers, body, y);

  const exportContext = { tests, assessmentId, filters };
  if (hasPedagogyExportData(data, exportContext)) {
    const pedagogyColumns = collectPedagogyColumns(data, exportContext);
    addA4PdfPage(doc, 'portrait');
    let py = drawPdfReportTitle(doc, 'PEDAGOGY-WISE MARKS REPORT');
    py = drawPdfGroupSummaryMatrix(doc, groupColumns, py);
    py += 4;

    const pedagogyHeaders = ['S.No', 'Student Name', ...pedagogyColumns, 'Total Marks'];
    const pedagogyGroups = groupExportEntries(data);
    const sortPedagogyGroup = (groupData) =>
      sortPedagogyCandidatesForExport(groupData, pedagogyColumns);
    const sortedAllPedagogyData = flattenSortedExportGroups(pedagogyGroups, sortPedagogyGroup);
    const pedagogyBody = sortedAllPedagogyData.map((row, index) => {
      const pedagogyScores = pedagogyColumns.map((label) =>
        getPedagogyMarkForColumn(row.pedagogy_marks, label)
      );
      const pedagogyTotal = pedagogyScores.reduce((sum, score) => sum + (Number(score) || 0), 0);
      return [index + 1, row.candidate_name || 'Anonymous', ...pedagogyScores, pedagogyTotal];
    });
    drawPdfDataTable(doc, pedagogyHeaders, pedagogyBody, py);
  }

  const logoDataUrl = await getEnovaLogoDataUrl();
  applyAssessmentPdfBranding(doc, logoDataUrl);

  const filename = `Assessment_Report_${sanitizeReportFilename(meta.testName)}_${Date.now()}.pdf`;
  return { doc, filename };
};

const downloadAssessmentReportPdf = async (params) => {
  const { doc, filename } = await buildAssessmentReportPdfDoc(params);
  doc.save(filename);
};

const ExportFormatIconButton = ({ label, onClick, disabled, children }) => (
  <button
    type="button"
    className="export-format-icon-btn"
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
  >
    {children}
    <span className="export-format-icon-btn__label">{label}</span>
  </button>
);

const ExportPdfButton = ({ onClick, disabled, label = 'Preview PDF' }) => (
  <ExportFormatIconButton label={label} onClick={onClick} disabled={disabled}>
    <ExportPdfIcon />
  </ExportFormatIconButton>
);

const ExportExcelButton = ({ onClick, disabled }) => (
  <ExportFormatIconButton label="Download Excel" onClick={onClick} disabled={disabled}>
    <ExportExcelIcon />
  </ExportFormatIconButton>
);

// --- Export Report Modal ---

const ExportReportModal = ({
  isOpen,
  onClose,
  data,
  reportMeta,
  tests = [],
  assessmentId,
  exportFilters = {},
}) => {
  const allFields = [
    { id: 'candidate_name', label: 'Candidate Name' },
    { id: 'test_name', label: 'Test Name' },
    { id: 'test_type', label: 'Test Type' },
    { id: 'group_name', label: 'Group' },
    { id: 'displayStatus', label: 'Status' },
    { id: 'marks_text', label: 'Marks' },
    { id: 'final_score', label: 'Score' },
    { id: 'overall_pct', label: 'Accuracy (%)' },
    { id: 'status', label: 'Result' },
    { id: 'date_ymd', label: 'Date' },
    { id: 'duration_text', label: 'Duration' }
  ];

  const [selectedFields, setSelectedFields] = useState(allFields.map(f => f.id));
  const [searchTerm, setSearchTerm] = useState('');
  const [pdfPreview, setPdfPreview] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setPdfPreview((prev) => {
        revokeAssessmentPdfPreview(prev);
        return null;
      });
    }
  }, [isOpen]);

  const clearPdfPreview = useCallback(() => {
    setPdfPreview((prev) => {
      revokeAssessmentPdfPreview(prev);
      return null;
    });
  }, []);

  if (!isOpen) return null;

  const filteredFields = allFields.filter(f => 
    f.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleField = (id) => {
    setSelectedFields(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  const allSelected = selectedFields.length === allFields.length;
  const handleToggleSelectAll = () => {
    setSelectedFields(allSelected ? [] : allFields.map(f => f.id));
  };

  const validateExport = () => {
    if (selectedFields.length === 0) {
      toast.error('Please select at least one field to export.');
      return false;
    }
    return true;
  };

  const exportPayload = {
    data,
    allFields,
    selectedFieldIds: selectedFields,
    reportMeta,
    tests,
    assessmentId,
    filters: exportFilters,
  };

  const handleExportExcel = async () => {
    if (!validateExport()) return;
    try {
      await downloadAssessmentReportExcel({ ...exportPayload, exportType: 'excel' });
      toast.success('Excel report downloaded successfully', { position: 'top-right' });
      onClose();
    } catch (err) {
      console.error('Excel export failed:', err);
      toast.error(err?.message || 'Failed to export Excel report', { position: 'top-right' });
    }
  };

  const handleExportPdf = async () => {
    if (!validateExport()) return;

    try {
      const { doc, filename } = await buildAssessmentReportPdfDoc(exportPayload);
      setPdfPreview((prev) => {
        revokeAssessmentPdfPreview(prev);
        return createAssessmentPdfPreview(doc, filename);
      });
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error(err?.message || 'Failed to generate PDF report');
    }
  };

  const handleDownloadPdfPreview = () => {
    downloadAssessmentPdfPreview(pdfPreview);
    toast.success('PDF report downloaded successfully');
  };

  return (
    <>
      <AssessmentPdfFullscreenViewer
        preview={pdfPreview}
        onClose={clearPdfPreview}
        onDownload={handleDownloadPdfPreview}
      />
    <div className="assessment-popup-overlay" style={{ zIndex: 9999999 }} role="dialog" aria-modal="true" aria-labelledby="export-report-modal-title">
      <div className="export-modal-v3 rounded-[24px] overflow-hidden w-[550px] animate-scale-in">
        <div className="export-header-v3">
          <div className="export-header-v3__leading">
            <div className="export-icon-v3" aria-hidden>
              <i className="bi bi-file-earmark-spreadsheet-fill"></i>
            </div>
            <div className="export-title-v3">
              <h4 id="export-report-modal-title">EXPORT REPORT</h4>
              <p>CHOOSE FIELDS TO EXPORT</p>
            </div>
          </div>
          <div className="export-header-v3__actions">
            <div className="export-format-actions export-format-actions--header">
              <ExportPdfButton
                onClick={handleExportPdf}
                disabled={selectedFields.length === 0}
                label="Preview PDF"
              />
              <ExportExcelButton
                onClick={handleExportExcel}
                disabled={selectedFields.length === 0}
              />
            </div>
            <button
              type="button"
              onClick={onClose}
              className="export-header-v3__close-btn"
              aria-label="Cancel"
              title="Cancel"
            >
              <i className="bi bi-x-lg" aria-hidden />
            </button>
          </div>
        </div>

        <div className="export-body-v3">
          <div className="flex justify-between items-center mb-6">
             <div className="text-[13px] font-black text-slate-800">Select Columns</div>
             <div className="export-search-v3 !mb-0 w-[180px]">
                <i className="bi bi-search"></i>
                <input 
                  type="text" 
                  placeholder="Search..." 
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
             </div>
          </div>

          <div className="export-grid-v3">
            {filteredFields.map(f => (
              <div 
                key={f.id} 
                className="export-item-v3"
                onClick={() => toggleField(f.id)}
                style={{ 
                  background: selectedFields.includes(f.id) ? '#fff5f5' : '#f8fafc',
                  borderColor: selectedFields.includes(f.id) ? '#fee2e2' : '#f1f5f9'
                }}
              >
                <span style={{ 
                  background: selectedFields.includes(f.id) ? '#b91c1c' : '#cbd5e1' 
                }}>
                  {selectedFields.includes(f.id) && <i className="bi bi-check-lg"></i>}
                </span>
                {f.label}
              </div>
            ))}
          </div>

          <div className="export-footer-actions-v3">
             <button
               type="button"
               className="select-all-v3"
               onClick={handleToggleSelectAll}
               aria-label={allSelected ? 'Deselect all columns' : 'Select all columns'}
               title={allSelected ? 'Deselect all columns' : 'Select all columns'}
             >
               <span
                 className={`select-all-v3__box ${allSelected ? 'select-all-v3__box--checked' : ''}`}
                 aria-hidden
               >
                 {allSelected && <i className="bi bi-check-lg text-[10px]"></i>}
               </span>
               <span className="select-all-v3__label">SELECT ALL</span>
             </button>
             <div className="selection-count-v3">
               {selectedFields.length} OF {allFields.length} SELECTED
             </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// --- Modals / Overlays ---

const EvaluationView = ({
  candidate,
  onClose,
  onSave,
  onNext,
  onPrev,
  isFirst,
  isLast,
  isEditMode = false,
  isViewOnly = false,
}) => {
  const evalScrollRef = useRef(null);
  const answerSheetItems = candidate.answer_sheet_items || [];
  const [evaluation, setEvaluation] = useState(
    candidate.subjective_items?.map(item => ({
      ...item,
      awarded: candidate.displayStatus === 'Evaluated' ? (item.awarded_marks ?? '') : '',
    })) || []
  );
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    const scrollEl = evalScrollRef.current;
    if (!scrollEl) return undefined;

    const onWheel = (e) => {
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement &&
        active.classList.contains('assess-mod-eval-mark-input') &&
        scrollEl.contains(active)
      ) {
        e.preventDefault();
        scrollEl.scrollTop += e.deltaY;
      }
    };

    scrollEl.addEventListener('wheel', onWheel, { passive: false });
    return () => scrollEl.removeEventListener('wheel', onWheel);
  }, []);

  const validate = () => {
    const missingIndices = [];
    evaluation.forEach((item, idx) => {
      if (item.awarded === "" || item.awarded === null || item.awarded === undefined) {
        missingIndices.push(idx);
      }
    });
    setErrors(missingIndices);
    return missingIndices.length === 0;
  };

  const handleMarkChange = (index, value) => {
    const updated = [...evaluation];
    const numVal = value === "" ? "" : Math.min(updated[index].marks, Math.max(0, parseFloat(value) || 0));
    updated[index].awarded = numVal;
    setEvaluation(updated);
    if (value !== "") {
      setErrors(prev => prev.filter(i => i !== index));
    }
  };

  const totalAwarded = evaluation.reduce((sum, item) => sum + (parseFloat(item.awarded) || 0), 0);
  const sheetTotalAwarded = answerSheetItems.reduce(
    (sum, item) => sum + (parseFloat(item.awarded_marks) || 0),
    0
  );
  const obtainedMarks =
    candidate.obtained_marks ??
    (Number(candidate.mcq_score) || 0) +
      (isViewOnly ? sheetTotalAwarded : totalAwarded);

  const handleAction = (callback) => {
    if (validate()) {
      callback(evaluation);
    } else {
      toast.error('Please provide marks for all subjective questions.');
      const firstError = document.querySelector('.error-pulse');
      if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  if (isViewOnly) {
    return (
      <div className="assess-mod-eval-view assess-mod-eval-view--readonly flex flex-col flex-1 min-h-0 h-full bg-white rounded-[24px] border border-slate-100 results-card-lg overflow-hidden animate-scale-in">
        <div className="assess-mod-eval-header shrink-0 px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={onClose}
              className="assess-mod-eval-back-btn w-10 h-10 rounded-full bg-white border flex items-center justify-center transition-all shadow-sm"
              aria-label="Back to results"
            >
              <i className="bi bi-arrow-left"></i>
            </button>
            <div>
              <h3 className="text-xl font-black text-slate-800 mb-1">Answer Sheet</h3>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest flex flex-wrap items-center gap-x-1 gap-y-1">
                <span>Evaluated submission for</span>
                <span className="assess-mod-eval-candidate-name">{candidate.candidate_name}</span>
                <SubmissionFileLinks files={candidate.submission_files} compact />
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
              <i className="bi bi-patch-check-fill text-[11px]"></i>
              <span className="text-[10px] font-black uppercase tracking-widest">Evaluated</span>
            </span>
            <span className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-xl text-[10px] font-black uppercase border border-indigo-100">
              {candidate.test_name}
            </span>
          </div>
        </div>

        <div ref={evalScrollRef} className="assess-mod-eval-scroll flex-1 min-h-0 overflow-y-auto">
          <div className="assess-mod-eval-grid assess-mod-eval-grid--sheet sticky top-0 z-10 grid grid-cols-[1.1fr_1.4fr_1.2fr_100px] gap-5 px-8 py-4 bg-slate-50/90 backdrop-blur-md border-b border-slate-100 shadow-sm">
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Question</span>
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Candidate Answer</span>
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Model / Correct</span>
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest text-center">Marks</span>
          </div>

          <div className="p-2 space-y-1">
            {answerSheetItems.length > 0 ? (
              answerSheetItems.map((item, i) => (
                <div
                  key={item.id ?? i}
                  className="assess-mod-eval-grid-row assess-mod-eval-grid-row--sheet grid grid-cols-[1.1fr_1.4fr_1.2fr_100px] gap-5 px-6 py-4 border-b border-slate-50 items-start hover:bg-slate-50/80 transition-all rounded-xl mx-2"
                >
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-[11px] font-black text-slate-500">
                        #{i + 1}
                      </span>
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-lg text-[9px] font-black uppercase tracking-widest border border-slate-200">
                        {item.type}
                      </span>
                      <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-lg text-[9px] font-black uppercase border border-indigo-100">
                        {item.marks} pts
                      </span>
                    </div>
                    <h4 className="text-[14px] font-bold text-slate-700 leading-relaxed">{item.question}</h4>
                  </div>

                  <div
                    className={`assess-mod-eval-response rounded-2xl p-4 border results-card-sm ${
                      item.type === 'MCQ' && item.is_correct === false
                        ? 'bg-rose-50/50 border-rose-100'
                        : item.type === 'MCQ' && item.is_correct
                          ? 'bg-emerald-50/50 border-emerald-100'
                          : 'bg-white border-slate-100'
                    }`}
                  >
                    <p className="assess-mod-eval-response-text text-[13px] text-slate-700 font-medium leading-relaxed m-0">
                      {item.candidate_answer}
                    </p>
                  </div>

                  <div className="assess-mod-eval-response bg-slate-50 border border-slate-100 rounded-2xl p-4 results-card-sm">
                    <p className="assess-mod-eval-response-text text-[13px] text-slate-600 font-medium leading-relaxed m-0">
                      {item.model_answer}
                    </p>
                  </div>

                  <div className="flex flex-col items-center gap-2 pt-1">
                    <div className="text-center">
                      <span className="text-lg font-black text-indigo-600">{item.awarded_marks}</span>
                      <span className="text-[12px] font-black text-slate-300"> / {item.marks}</span>
                    </div>
                    <span className="badge-pill badge-evaluated">Evaluated</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-24 text-center">
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                  No answer sheet data available.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="assess-mod-eval-footer shrink-0 px-8 py-5 border-t border-slate-100 bg-white flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex flex-col">
              <div className="text-[18px] font-black text-indigo-600 leading-none">{obtainedMarks}</div>
              <div className="text-[8px] font-black text-slate-400 uppercase tracking-widest mt-1.5">Total Score</div>
            </div>
          </div>
          <button type="button" className="btn-assessment btn-assessment-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="assess-mod-eval-view flex flex-col flex-1 min-h-0 h-full bg-white rounded-[24px] border border-slate-100 results-card-lg overflow-hidden animate-scale-in">
      <div className="assess-mod-eval-header shrink-0 px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center gap-4">
          <button 
            type="button"
            onClick={onClose}
            className="assess-mod-eval-back-btn w-10 h-10 rounded-full bg-white border flex items-center justify-center transition-all shadow-sm"
            aria-label="Back to results"
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <div>
            <h3 className="text-xl font-black text-slate-800 mb-1">
              {isEditMode ? 'Edit Evaluation' : 'Manual Evaluation'}
            </h3>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest flex flex-wrap items-center gap-x-1 gap-y-1">
              <span>{isEditMode ? 'Updating marks for' : 'Grading subjective answers for'}</span>
              <span className="assess-mod-eval-candidate-name">{candidate.candidate_name}</span>
              <SubmissionFileLinks files={candidate.submission_files} compact />
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isEditMode && (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
              <i className="bi bi-patch-check-fill text-[11px]"></i>
              <span className="text-[10px] font-black uppercase tracking-widest">Evaluated</span>
            </span>
          )}
          <span className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-xl text-[10px] font-black uppercase border border-indigo-100">
            {candidate.test_name}
          </span>
        </div>
      </div>

        <div
          ref={evalScrollRef}
          className="assess-mod-eval-scroll flex-1 min-h-0 overflow-y-auto"
        >
          <div className="assess-mod-eval-grid sticky top-0 z-10 grid grid-cols-[1.2fr_2fr_130px] gap-6 px-8 py-4 bg-slate-50/90 backdrop-blur-md border-b border-slate-100 shadow-sm">
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Question & Context</span>
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Candidate Response</span>
            <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest text-center">Marking</span>
          </div>

          <div className="p-2 space-y-1">
            {evaluation.length > 0 ? (
              evaluation.map((item, i) => (
                <div key={i} className="assess-mod-eval-grid-row grid grid-cols-[1.2fr_2fr_130px] gap-6 px-6 py-4 border-b border-slate-50 items-start hover:bg-indigo-50/20 transition-all rounded-xl mx-2">
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-[11px] font-black text-slate-500">#{i + 1}</span>
                      <span className="px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[9px] font-black uppercase tracking-widest border border-indigo-100">{item.marks} Points</span>
                    </div>
                    <h4 className="text-[14px] font-bold text-slate-700 leading-relaxed line-clamp-3">{item.question}</h4>
                  </div>

                  <div className="assess-mod-eval-response bg-white border border-slate-100 rounded-2xl p-4 h-32 results-card-sm">
                    <p className="assess-mod-eval-response-text text-[13px] text-slate-600 font-medium leading-relaxed italic">
                      {item.candidate_answer ? `"${item.candidate_answer}"` : "No response provided."}
                    </p>
                  </div>

                  <div className="flex flex-col items-center gap-3 pt-1">
                    <div className={`flex items-center gap-3 p-2.5 rounded-2xl transition-all ${errors.includes(i) ? 'bg-rose-50 border-rose-200 border error-pulse' : ''}`}>
                      <EvalMarksInput
                        className="w-16 h-11 bg-white border rounded-xl px-2 text-md font-black text-center shadow-sm focus:ring-4 focus:ring-indigo-50 outline-none transition-all"
                        value={item.awarded}
                        max={item.marks}
                        onChange={(v) => handleMarkChange(i, v)}
                        onFocus={(e) => {
                          if (e.target.value === '0') handleMarkChange(i, '');
                        }}
                      />
                      <span className="text-[12px] font-black text-slate-300">/ {item.marks}</span>
                    </div>
                    
                    <span className={`badge-pill ${item.awarded !== "" ? 'badge-evaluated' : 'badge-pending'}`}>
                      {item.awarded !== "" ? 'Evaluated' : 'Pending'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-24 text-center">
                 <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">No manual review required.</p>
              </div>
            )}
          </div>
        </div>

        <div className="assess-mod-eval-footer shrink-0 px-8 py-5 border-t border-slate-100 bg-white flex items-center justify-between">
          <div className="flex items-center gap-6">
             {evaluation.length > 0 && (
               <>
                 <div className="flex flex-col">
                    <div className="text-[18px] font-black text-indigo-600 leading-none">{totalAwarded}</div>
                    <div className="text-[8px] font-black text-slate-400 uppercase tracking-widest mt-1.5">Awarded</div>
                 </div>
                 <div className="w-px h-10 bg-slate-100"></div>
                 <div className="flex flex-col">
                    <div className="text-[18px] font-black text-slate-800 leading-none">
                      {(candidate.mcq_score || 0) + totalAwarded}
                      <span className="text-[12px] text-slate-300 ml-1">/ {candidate.total_marks}</span>
                    </div>
                    <div className="text-[8px] font-black text-slate-500 uppercase tracking-widest mt-1.5">Total Score</div>
                 </div>
               </>
             )}
          </div>
          <div className="flex items-center gap-3">
            {!isEditMode && !isFirst && evaluation.length > 0 && (
              <button 
                className="btn-assessment btn-assessment-secondary" 
                onClick={() => handleAction(onPrev)}
              >
                PREVIOUS
              </button>
            )}
            {evaluation.length > 0 ? (
              isEditMode ? (
                <>
                  {!isFirst && (
                    <button
                      type="button"
                      className="btn-assessment btn-assessment-secondary"
                      onClick={() => handleAction(onPrev)}
                    >
                      PREVIOUS
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-assessment btn-assessment-danger"
                    onClick={() => handleAction(onSave)}
                  >
                    Update
                  </button>
                  {!isLast && (
                    <button
                      type="button"
                      className="btn-assessment btn-assessment-secondary"
                      onClick={() => handleAction(onNext)}
                    >
                      NEXT
                    </button>
                  )}
                </>
              ) : isLast ? (
                <button 
                  type="button"
                  className="btn-assessment btn-assessment-danger" 
                  onClick={() => handleAction(onSave)}
                >
                  Submit
                </button>
              ) : (
                <button 
                  type="button"
                  className="btn-assessment btn-assessment-danger" 
                  onClick={() => handleAction(onNext)}
                >
                  SAVE & NEXT
                </button>
              )
            ) : (
              <button 
                className="btn-assessment btn-assessment-secondary" 
                onClick={onClose}
              >
                CLOSE
              </button>
            )}
          </div>
        </div>
      </div>
  );
};

const PAGE_SIZE = 6;

// --- Main Page Component ---

const ResultsEvaluation = ({ onBack, assessmentId }) => {
  const { navigateToModule } = useAssessmentContext();
  const { tests } = useTests();
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [evaluatingCandidate, setEvaluatingCandidate] = useState(null);
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });
  const [showExportModal, setShowExportModal] = useState(false);
  const [successBanner, setSuccessBanner] = useState(null);

  const [filters, setFilters] = useState({
    type: '',
    testName: '',
    group: '',
    role: '',
    date: '',
    sortConfig: { key: 'final_score', direction: 'desc' },
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    if (assessmentId && tests.length > 0) {
      const test = tests.find(t => String(t.id) === String(assessmentId));
      if (test) setFilters(prev => ({ ...prev, testName: test.name }));
    }
  }, [assessmentId, tests]);

  useEffect(() => {
    if (!successBanner) return undefined;
    const timer = window.setTimeout(() => setSuccessBanner(null), 8000);
    return () => window.clearTimeout(timer);
  }, [successBanner]);

  const loadFlow = useCallback(async () => {
      setIsLoading(true);
      try {
        const dbResp = await testService.getSubmissions(assessmentId || '');
        const rawSubmissions = dbResp.submissions || [];
        const testMap = {};
        tests.forEach(t => { testMap[t.id] = t; });

        const processedResults = rawSubmissions.map(sub => {
          try {
            const allAnswers = JSON.parse(sub.answers_json || '{}');
            const test = testMap[sub.assessment_id] || tests.find(t => t.id == sub.assessment_id) || { name: 'Standard Test', test_packs: [] };
            const pack = test.test_packs?.find(p => p.id == sub.test_pack_id);
            const questions = pack?.questions || [];
            
            const subjectiveItems = questions
              .filter(q => q.type !== 'MCQ')
              .map(q => ({
                id: q.id,
                question: q.question || q.text,
                marks: parseFloat(q.marks) || 1,
                candidate_answer: allAnswers[q.id] || allAnswers[q.question] || "No answer provided",
                awarded_marks: parseFloat(allAnswers[`awarded_${q.id}`]) || 0
              }));

            const rawMcq = parseFloat(sub.mcq_score) || 0;
            const subjectiveAwarded = subjectiveItems.reduce((sum, q) => sum + (parseFloat(q.awarded_marks) || 0), 0);
            const totalMarks = questions.reduce((sum, q) => sum + (parseFloat(q.marks) || 0), 0);
            
            const answer_sheet_items = buildAnswerSheetItems(questions, allAnswers);

            const templatePassMark = parseFloat(pack?.template?.pass_mark);
            const templateTotalMarks = parseFloat(pack?.template?.total_marks);
            let passPercentage = 50;
            if (!isNaN(templatePassMark) && !isNaN(templateTotalMarks) && templateTotalMarks > 0) {
              passPercentage = Math.round((templatePassMark / templateTotalMarks) * 100);
            } else if (pack?.pass_mark) {
              passPercentage = parseInt(pack.pass_mark, 10) || 50;
            } else if (test?.pass_mark) {
              passPercentage = parseInt(test.pass_mark, 10) || 50;
            }

            const finalScore = sub.status === 'Evaluated' ? rawMcq : (rawMcq + subjectiveAwarded);
            const mcqScore = sub.status === 'Evaluated' ? (rawMcq - subjectiveAwarded) : rawMcq;
            const overallPct = totalMarks > 0 ? Math.round((finalScore / totalMarks) * 100) : (parseFloat(sub.overall_pct) || 0);
            const isPass = overallPct >= passPercentage;
            const isMcqOnly = questions.length > 0 && subjectiveItems.length === 0;

            const calculateDuration = (start, end) => {
              if (!start) return 0;
              const s = new Date(start);
              const e = end ? new Date(end) : new Date();
              if (isNaN(s.getTime()) || isNaN(e.getTime())) return 0;
              const diff = Math.floor((e.getTime() - s.getTime()) / 1000);
              return diff > 0 ? diff : 0;
            };

            const formatDuration = (seconds) => {
              if (!seconds) return '0s';
              const h = Math.floor(seconds / 3600);
              const m = Math.floor((seconds % 3600) / 60);
              const s = seconds % 60;
              
              if (h > 0) return `${h}h ${m}m ${s}s`;
              if (m > 0) return `${m}m ${s}s`;
              return `${s}s`;
            };

            const durationSec = calculateDuration(sub.started_at, sub.submitted_at);
            const isOngoing = !sub.submitted_at;
            const displayStatus = isOngoing ? 'Ongoing' : ((isMcqOnly || sub.status === 'Evaluated') ? 'Evaluated' : 'Pending Review');

            const submissionFiles = normalizeSubmissionFilesList(sub.submission_files);

            return {
              key: `${sub.id}_${sub.submitted_at}`,
              testId: sub.assessment_id,
              candidate_name: sub.candidate_name || 'Anonymous',
              submission_files: submissionFiles,
              test_type: test.category || test.type || 'General',
              test_name: test.name,
              role: sub.role || 'Candidate',
              group_name: sub.group_name || 'General Group',
              date_ymd: sub.submitted_at ? sub.submitted_at.split(' ')[0] : 'N/A',
              status: isPass ? 'Pass' : 'Fail',
              displayStatus,
              marks_text: `${finalScore}/${totalMarks}`,
              final_score: finalScore,
              total_marks: totalMarks,
              total_questions: questions.length,
              overall_pct: overallPct,
              pass_percentage: passPercentage,
              subjective_items: subjectiveItems,
              mcq_score: mcqScore,
              isMcqOnly,
              duration_sec: durationSec,
              duration_text: formatDuration(durationSec),
              pedagogy_marks: computePedagogyMarksForSubmission(questions, allAnswers),
              pack_questions: questions,
              answer_sheet_items,
              rawSub: sub
            };
          } catch (e) { return null; }
        }).filter(Boolean);
        
        setResults(processedResults);
      } catch (err) { console.error(err); } finally { setIsLoading(false); }
  }, [tests, assessmentId]);

  useEffect(() => { loadFlow(); }, [loadFlow]);

  const filterOptions = useMemo(() => ({
    types: [...new Set(results.map(r => r.test_type))].filter(Boolean),
    tests: [...new Set(results.map(r => r.test_name))].filter(Boolean),
    groups: [...new Set(results.map(r => r.group_name))].filter(Boolean),
    roles: [...new Set(results.map(r => r.role))].filter(Boolean).sort((a, b) => a.localeCompare(b)),
    dates: [...new Set(results.map(r => r.date_ymd))].filter(Boolean),
  }), [results]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.type, filters.testName, filters.group, filters.role, filters.date, filters.search, filters.sortConfig]);

  const filteredData = useMemo(() => {
    let result = results.filter(item => {
      if (assessmentId && String(item.testId) !== String(assessmentId)) return false;
      if (filters.type && item.test_type !== filters.type) return false;
      if (!assessmentId && filters.testName && item.test_name !== filters.testName) return false;
      if (filters.group && item.group_name !== filters.group) return false;
      if (filters.role && item.role !== filters.role) return false;
      if (filters.date && item.date_ymd !== filters.date) return false;
      if (filters.search && !item.candidate_name.toLowerCase().includes(filters.search.toLowerCase())) return false;
      return true;
    });
    const { key, direction } = filters.sortConfig;
    result.sort((a, b) => {
      let valA = a[key];
      let valB = b[key];
      
      // Handle numeric comparisons
      if (typeof valA === 'number' && typeof valB === 'number') {
        return direction === 'asc' ? valA - valB : valB - valA;
      }
      
      // Handle string comparisons
      valA = String(valA || '').toLowerCase();
      valB = String(valB || '').toLowerCase();
      
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [results, filters, assessmentId]);

  const stats = useMemo(() => {
    const totalMark = filteredData.length > 0 ? Math.max(...filteredData.map(r => r.total_marks)) : 0;
    const finalResults = filteredData.filter(r => r.displayStatus !== 'Pending Review');
    const passCount = finalResults.filter(r => r.status === 'Pass').length;
    const failCount = finalResults.filter(r => r.status === 'Fail').length;
    const pendingCount = results.filter(r => r.displayStatus === 'Pending Review').length;
    return {
      totalMark,
      passPct: finalResults.length > 0 ? Math.round((passCount / finalResults.length) * 100) : 0,
      failCount,
      pendingCount,
    };
  }, [filteredData, results]);

  const effectivePageSize = pageSize === 'All' ? filteredData.length || 1 : pageSize;
  const totalPages = Math.max(1, Math.ceil(filteredData.length / effectivePageSize));

  const paginatedData = useMemo(() => {
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * effectivePageSize;
    return filteredData.slice(start, start + effectivePageSize);
  }, [filteredData, currentPage, totalPages, effectivePageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSaveEvaluation = async (evaluationData, options = {}) => {
    const showSuccess = typeof options === 'boolean' ? options : (options.showSuccess ?? true);
    const isUpdate = typeof options === 'boolean' ? false : Boolean(options.isUpdate);
    const candidateName = evaluatingCandidate?.candidate_name || 'Candidate';
    try {
      if (showSuccess) setIsLoading(true);
      const subjectiveAwarded = evaluationData.reduce((sum, item) => sum + (parseFloat(item.awarded) || 0), 0);
      const payload = {
        submission_id: evaluatingCandidate.rawSub.id,
        evaluations: evaluationData.map(e => ({ id: e.id, awarded: parseFloat(e.awarded) || 0 })),
        final_score: (parseFloat(evaluatingCandidate.mcq_score) || 0) + subjectiveAwarded,
        total_marks: evaluatingCandidate.total_marks,
        status: 'Evaluated'
      };
      const response = await testService.updateSubmissionScore(payload);
      if (response.status === 'success' || response.status === true) {
        if (showSuccess) {
          setSuccessBanner({
            title: isUpdate ? 'Evaluation updated successfully!' : 'All evaluations submitted successfully!',
            message: isUpdate
              ? `Marks for ${candidateName} have been updated.`
              : `Subjective answers for ${candidateName} have been graded and saved.`,
          });
          toast.success(
            isUpdate ? 'Evaluation updated successfully!' : 'All evaluations submitted successfully!',
            { position: 'top-right', duration: 5000 }
          );
          setEvaluatingCandidate(null);
        }
        loadFlow();
      } else if (showSuccess) {
        toast.error(response?.message || 'Failed to save evaluation.', { position: 'top-right' });
      }
    } catch (err) {
      console.error(err);
      if (showSuccess) {
        toast.error(err?.response?.data?.message || 'Failed to save evaluation.', { position: 'top-right' });
      }
    } finally {
      if (showSuccess) setIsLoading(false);
    }
  };

  const evalPanelMode = evaluatingCandidate?._evalPanel
    ?? (evaluatingCandidate?.displayStatus === 'Evaluated' ? 'edit' : 'grade');
  const isEvalViewOnly = evalPanelMode === 'view';
  const isEvalEditMode = evalPanelMode === 'edit';

  const handleGenerateReport = () => {
    setShowExportModal(true);
  };

  const exportReportMeta = useMemo(() => {
    const activeTest =
      (assessmentId && tests.find((t) => String(t.id) === String(assessmentId))) || null;
    const totalQuestions = filteredData.length
      ? Math.max(...filteredData.map((r) => Number(r.total_questions) || 0))
      : 0;
    const examType =
      filters.type ||
      filteredData[0]?.test_type ||
      activeTest?.category ||
      activeTest?.type ||
      'General';

    return resolveAssessmentReportMeta({
      tests,
      assessmentId,
      filters,
      data: filteredData,
      totalQuestions,
      examType,
    });
  }, [tests, assessmentId, filters, filteredData]);

  return (
    <div
      className={`assess-mod-page assess-mod-results-page results-evaluation-container flex flex-col w-full ${
        evaluatingCandidate ? 'assess-mod-results-eval-active' : 'assess-mod-results-list'
      }`}
    >
      <div
        className={`assess-mod-results-header assess-mod-header shrink-0 flex items-start justify-between ${
          evaluatingCandidate ? 'mb-3' : 'mb-6'
        }`}
      >
        <div className="assess-mod-header-brand pt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="assess-mod-back-mobile lg:hidden mt-1"
            aria-label="Back"
            {...assessTipProps('Back')}
          >
            <FiArrowLeft className="text-lg" />
          </button>
          <h3 className="text-[32px] font-black text-slate-800 tracking-tight m-0 max-lg:text-xl">Results & Evaluation</h3>
        </div>
        <div className={`assess-mod-results-header-right flex flex-col items-end ${evaluatingCandidate ? 'gap-2' : 'gap-4'}`}>
          <div className="assess-mod-header-actions flex items-center gap-3">
            <button 
              type="button"
              className="btn-assessment btn-assessment-primary hidden lg:inline-flex" 
              onClick={() => navigateToModule('analytics', 'evaluation')}
            >
              <i className="bi bi-graph-up text-sm"></i> View Analytics
            </button>
            <button 
              type="button"
              className="btn-assessment btn-assessment-danger hidden lg:inline-flex" 
              onClick={onBack}
            >
              <i className="bi bi-arrow-left text-sm"></i> Back
            </button>
          </div>
          {!evaluatingCandidate && <StatsCards stats={stats} />}
        </div>
      </div>

      {successBanner && (
        <div
          className="assess-mod-eval-success-banner shrink-0 mb-3 flex items-start gap-3 px-5 py-4 rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white shadow-sm animate-in fade-in slide-in-from-top-2 duration-300"
          role="status"
          aria-live="polite"
        >
          <FiCheckCircle className="text-emerald-600 text-xl shrink-0 mt-0.5" aria-hidden />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black text-emerald-800 mb-0.5">{successBanner.title}</p>
            <p className="text-xs font-bold text-emerald-700/90 leading-relaxed m-0">{successBanner.message}</p>
          </div>
          <button
            type="button"
            onClick={() => setSuccessBanner(null)}
            className="shrink-0 w-8 h-8 rounded-lg text-emerald-600 hover:bg-emerald-100 transition-colors flex items-center justify-center"
            aria-label="Dismiss success message"
          >
            <i className="bi bi-x-lg text-xs" />
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="py-24 text-center">
          <div className="w-10 h-10 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
        </div>
      ) : evaluatingCandidate ? (
        <div className="assess-mod-eval-shell flex flex-col flex-1 min-h-0 overflow-hidden">
          <EvaluationView 
            key={`${evaluatingCandidate.key}-${evalPanelMode}`}
            candidate={evaluatingCandidate} 
            onClose={() => setEvaluatingCandidate(null)} 
            isViewOnly={isEvalViewOnly}
            isEditMode={isEvalEditMode}
            onSave={(evalData) => handleSaveEvaluation(evalData, { showSuccess: true, isUpdate: isEvalEditMode })}
            onNext={async (evalData) => {
              await handleSaveEvaluation(evalData, { showSuccess: false });
              const idx = filteredData.findIndex(c => c.key === evaluatingCandidate.key);
              if (idx < filteredData.length - 1) setEvaluatingCandidate(filteredData[idx + 1]);
              else setEvaluatingCandidate(null);
            }}
            onPrev={async (evalData) => {
              if (isEvalEditMode) {
                await handleSaveEvaluation(evalData, { showSuccess: false });
              }
              const idx = filteredData.findIndex(c => c.key === evaluatingCandidate.key);
              if (idx > 0) setEvaluatingCandidate(filteredData[idx - 1]);
            }}
            isFirst={filteredData.findIndex(c => c.key === evaluatingCandidate.key) === 0}
            isLast={filteredData.findIndex(c => c.key === evaluatingCandidate.key) === filteredData.length - 1}
          />
        </div>
      ) : (
        <div className="leaderboard-container assess-mod-card assess-mod-results-leaderboard-panel">
          <div className="assess-mod-results-leaderboard-toolbar">
            <div className="assess-mod-results-leaderboard-head">
              <div className="flex items-center gap-4 flex-wrap">
                <h4 className="assess-mod-leaderboard-title" style={{ margin: 0 }}>CANDIDATE RANKING & LEADERBOARD</h4>
                <div className="flex items-center gap-1 bg-white border border-slate-200/80 rounded-xl px-2.5 py-1 text-xs shadow-sm">
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const val = e.target.value;
                      setPageSize(val === 'All' ? 'All' : Number(val));
                      setCurrentPage(1);
                    }}
                    className="bg-transparent border-0 focus:ring-0 focus:outline-none font-bold text-slate-700 cursor-pointer pr-5 text-[11px] py-0 line-height-none m-0"
                    style={{
                      minWidth: '40px',
                      backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='%2364748b' height='20' viewBox='0 0 24 24' width='20' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0px center',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      appearance: 'none',
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                      paddingTop: '0px',
                      paddingBottom: '0px'
                    }}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value="All">All</option>
                  </select>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest border-l border-slate-100 pl-2">Records</span>
                </div>
              </div>
              <div className="assess-mod-leaderboard-meta">
                <span className="assess-mod-candidate-count-badge">
                  {filteredData.length} Candidates
                </span>
                <button
                  type="button"
                  className="btn-assessment assess-mod-leaderboard-export-btn"
                  onClick={handleGenerateReport}
                >
                  <i className="bi bi-file-earmark-spreadsheet-fill" aria-hidden />
                  Export
                </button>
              </div>
            </div>

            <FilterBar
              filters={filters}
              onFilterChange={(k, v) => setFilters((p) => ({ ...p, [k]: v }))}
              onSearchChange={(s) => setFilters((p) => ({ ...p, search: s }))}
              options={filterOptions}
            />
          </div>
          <CandidateTable 
            data={paginatedData} 
            rankOffset={(Math.min(currentPage, totalPages) - 1) * effectivePageSize}
            onEvaluate={(candidate) => {
              setSuccessBanner(null);
              setEvaluatingCandidate({
                ...candidate,
                _evalPanel: candidate.displayStatus === 'Evaluated' ? 'edit' : 'grade',
              });
            }}
            onViewAnswerSheet={(candidate) => {
              setSuccessBanner(null);
              setEvaluatingCandidate({ ...candidate, _evalPanel: 'view' });
            }}
            sortConfig={filters.sortConfig}
            onSort={(key) => {
              setFilters(prev => ({
                ...prev,
                sortConfig: {
                  key,
                  direction: prev.sortConfig.key === key && prev.sortConfig.direction === 'desc' ? 'asc' : 'desc'
                }
              }));
            }}
          />
          <ResultsPagination
            currentPage={Math.min(currentPage, totalPages)}
            totalPages={totalPages}
            totalItems={filteredData.length}
            pageSize={effectivePageSize}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
      <AssessmentPopup 
        {...popupConfig}
        onCancel={() => setPopupConfig({ ...popupConfig, isOpen: false })}
      />
      <ExportReportModal 
        isOpen={showExportModal} 
        onClose={() => setShowExportModal(false)} 
        data={filteredData}
        reportMeta={exportReportMeta}
        tests={tests}
        assessmentId={assessmentId}
        exportFilters={filters}
      />
    </div>
  );
};

export default ResultsEvaluation;
